import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faBars,
    faBriefcase,
    faClipboardList,
    faCommentDots,
    faCog,
    faDatabase,
    faSearch,
    faUsers,
} from '@fortawesome/free-solid-svg-icons'
import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { MenuItems } from '../../../../enums/MenuItems'
import { AuthHelper } from '../../../../auth/AuthHelper'
import { useSafeUserData } from '../../../../stateStores/User'
import { UserRoles } from '../../../../api/tenant/models/UserDto'
import { Pages } from '../../../../Pages'
import SidebarItem from './SidebarItem'
import UserMenu from './UserMenu'

type Props = {
    activeMenuItem: MenuItems
    // When true, constrain the inner banner content to match page content width
    // Defaults to true to align with LayoutSingleView's max width
    restrictWidth?: boolean
}

export default function TopBanner({
    activeMenuItem,
    restrictWidth = true,
}: Props): React.JSX.Element {
    const { user, tenant } = useSafeUserData()
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const mobileMenuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(event.target as Node)
            ) {
                setMobileMenuOpen(false)
            }
        }

        if (mobileMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        } else {
            document.removeEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [mobileMenuOpen])

    const containerClass = `w-full ${
        restrictWidth ? 'max-w-[1040px] mx-auto px-4' : 'px-4'
    }`

    return (
        <header className="sticky top-0 z-50 border-b border-gray-200 bg-white">
            {/* Inner container to align with page content width */}
            <div className={containerClass}>
                <div className="flex items-center justify-between py-2">
                    {/* Left: Logo */}
                    <Link
                        to={Pages.Roles}
                        className="flex items-center gap-2 flex-shrink-0"
                        aria-label="Go to Roles"
                    >
                        <img
                            src="/Contxt_Logo_Full_BLK_CROPPED.png"
                            className="w-[100px] py-4"
                            alt="Contxt"
                        />
                    </Link>

                    {/* Center: Desktop nav only */}
                    <div className="hidden md:flex flex-grow ml-4">
                        <nav>
                            <ul className="flex gap-4">
                                {AuthHelper.userHasSomeRolesForTenant(
                                    user,
                                    [UserRoles.HM, UserRoles.TA],
                                    tenant.id,
                                ) && (
                                    <SidebarItem
                                        to={Pages.Roles}
                                        icon={faBriefcase}
                                        label="Roles"
                                        active={
                                            activeMenuItem === MenuItems.Roles
                                        }
                                    />
                                )}
                                {AuthHelper.userHasSomeRolesForTenant(
                                    user,
                                    [UserRoles.HM, UserRoles.TA],
                                    tenant.id,
                                ) && (
                                    <SidebarItem
                                        to={Pages.JobScreeners}
                                        icon={faClipboardList}
                                        label="Screeners"
                                        active={
                                            activeMenuItem ===
                                            MenuItems.JobScreeners
                                        }
                                    />
                                )}
                                {user.isProductAdmin && (
                                    <SidebarItem
                                        to={Pages.Tenants}
                                        icon={faUsers}
                                        label="Users"
                                        active={
                                            activeMenuItem === MenuItems.Users
                                        }
                                    />
                                )}
                                {user.isProductAdmin && (
                                    <SidebarItem
                                        to={Pages.DataManagement}
                                        icon={faDatabase}
                                        label="Manage Data"
                                        active={
                                            activeMenuItem ===
                                            MenuItems.DataManagement
                                        }
                                    />
                                )}
                                {/* {user.isProductAdmin && (
                            <SidebarItem
                                to={Pages.CandidateSearchVectorTests}
                                icon={faAddressCard}
                                label="Candidate Search"
                                active={
                                    activeMenuItem ===
                                    MenuItems.CandidateSearchVectorTests
                                }
                            />
                        )} */}
                                {user.isProductAdmin && (
                                    <SidebarItem
                                        to={Pages.HelixSearchV2}
                                        icon={faSearch}
                                        label="Search"
                                        active={
                                            activeMenuItem ===
                                            MenuItems.HelixSearch
                                        }
                                    />
                                )}
                                {user.isProductAdmin && (
                                    <SidebarItem
                                        to={Pages.LLMSettings}
                                        icon={faCog}
                                        label="LLM Settings"
                                        active={
                                            activeMenuItem ===
                                            MenuItems.LLMSettings
                                        }
                                    />
                                )}
                                {user.isProductAdmin && (
                                    <SidebarItem
                                        to={Pages.Kit}
                                        icon={faCommentDots}
                                        label="Kit"
                                        active={
                                            activeMenuItem === MenuItems.Kit
                                        }
                                    />
                                )}
                            </ul>
                        </nav>
                    </div>

                    {/* Right: Hamburger + UserMenu */}
                    <div className="flex items-center gap-3">
                        <button
                            className="md:hidden text-gray-600"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            <FontAwesomeIcon icon={faBars} size="lg" />
                        </button>
                        <div className="flex-shrink-0 relative">
                            <UserMenu />
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile dropdown nav */}
            {mobileMenuOpen && (
                <div
                    ref={mobileMenuRef}
                    className="md:hidden absolute top-full left-0 w-full bg-white border-b border-gray-200 z-40"
                >
                    <div className={containerClass}>
                        <ul className="flex flex-col gap-2 py-2">
                            {AuthHelper.userHasSomeRolesForTenant(
                                user,
                                [UserRoles.HM, UserRoles.TA],
                                tenant.id,
                            ) && (
                                <SidebarItem
                                    to={Pages.Roles}
                                    icon={faBriefcase}
                                    label="Roles"
                                    active={activeMenuItem === MenuItems.Roles}
                                />
                            )}
                            {AuthHelper.userHasSomeRolesForTenant(
                                user,
                                [UserRoles.HM, UserRoles.TA],
                                tenant.id,
                            ) && (
                                <SidebarItem
                                    to={Pages.JobScreeners}
                                    icon={faClipboardList}
                                    label="Screeners"
                                    active={
                                        activeMenuItem ===
                                        MenuItems.JobScreeners
                                    }
                                />
                            )}
                            {user.isProductAdmin && (
                                <>
                                    <SidebarItem
                                        to={Pages.Tenants}
                                        icon={faUsers}
                                        label="Users"
                                        active={
                                            activeMenuItem === MenuItems.Users
                                        }
                                    />
                                    <SidebarItem
                                        to={Pages.DataManagement}
                                        icon={faDatabase}
                                        label="Manage Data"
                                        active={
                                            activeMenuItem ===
                                            MenuItems.DataManagement
                                        }
                                    />
                                    <SidebarItem
                                        to={Pages.HelixSearchV2}
                                        icon={faSearch}
                                        label="Search"
                                        active={
                                            activeMenuItem ===
                                            MenuItems.HelixSearch
                                        }
                                    />
                                    <SidebarItem
                                        to={Pages.Kit}
                                        icon={faCommentDots}
                                        label="Kit"
                                        active={
                                            activeMenuItem === MenuItems.Kit
                                        }
                                    />
                                </>
                            )}
                        </ul>
                    </div>
                </div>
            )}
        </header>
    )
}
